import { useState, useEffect } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { WorkOrder, Equipment, User } from '@/types'

interface WorkOrderModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (workOrderData: Partial<WorkOrder>) => void
  workOrder?: WorkOrder | null
  equipment: Equipment[]
  users: User[]
  isLoading: boolean
  isEditMode: boolean
}

export default function WorkOrderModal({
  isOpen,
  onClose,
  onSave,
  workOrder,
  equipment,
  users,
  isLoading,
  isEditMode
}: WorkOrderModalProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium' as WorkOrder['priority'],
    status: 'pending' as WorkOrder['status'],
    type: 'corrective' as WorkOrder['type'],
    equipmentId: '',
    assignedTo: '',
    estimatedHours: 1,
    estimatedCost: 0,
    dueDate: '',
    scheduledStart: '',
    scheduledEnd: '',
    skillsRequired: [] as string[],
    progress: 0
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && workOrder) {
        setFormData({
          title: workOrder.title || '',
          description: workOrder.description || '',
          priority: workOrder.priority || 'medium',
          status: workOrder.status || 'pending',
          type: workOrder.type || 'corrective',
          equipmentId: workOrder.equipmentId || '',
          assignedTo: workOrder.assignedTo?.id || '',
          estimatedHours: workOrder.estimatedHours || 1,
          estimatedCost: workOrder.estimatedCost || 0,
          dueDate: workOrder.dueDate ? workOrder.dueDate.split('T')[0] : '',
          scheduledStart: workOrder.scheduledStart ? workOrder.scheduledStart.split('T')[0] : '',
          scheduledEnd: workOrder.scheduledEnd ? workOrder.scheduledEnd.split('T')[0] : '',
          skillsRequired: workOrder.skillsRequired || [],
          progress: workOrder.progress || 0
        })
      } else {
        setFormData({
          title: '',
          description: '',
          priority: 'medium',
          status: 'pending',
          type: 'corrective',
          equipmentId: '',
          assignedTo: '',
          estimatedHours: 1,
          estimatedCost: 0,
          dueDate: '',
          scheduledStart: '',
          scheduledEnd: '',
          skillsRequired: [],
          progress: 0
        })
      }
      setErrors({})
    }
  }, [isOpen, isEditMode, workOrder])

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required'
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required'
    }
    if (!formData.equipmentId) {
      newErrors.equipmentId = 'Equipment is required'
    }
    if (formData.estimatedHours <= 0) {
      newErrors.estimatedHours = 'Estimated hours must be greater than 0'
    }
    if (formData.estimatedCost < 0) {
      newErrors.estimatedCost = 'Estimated cost cannot be negative'
    }
    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    // Create work order data using camelCase field names (matching database schema)
    const workOrderData: Partial<WorkOrder> = {
      title: formData.title,
      description: formData.description,
      priority: formData.priority,
      status: formData.status,
      type: formData.type,
      equipmentId: formData.equipmentId,
      estimatedHours: formData.estimatedHours,
      estimatedCost: formData.estimatedCost,
      progress: formData.progress,
      dueDate: new Date(formData.dueDate).toISOString(),
      scheduledStart: formData.scheduledStart ? new Date(formData.scheduledStart).toISOString() : undefined,
      scheduledEnd: formData.scheduledEnd ? new Date(formData.scheduledEnd).toISOString() : undefined,
      skillsRequired: formData.skillsRequired
    }

    // Add assignedTo if specified
    if (formData.assignedTo) {
      const assignedUser = users.find(user => user.id === formData.assignedTo)
      if (assignedUser) {
        workOrderData.assignedTo = {
          id: assignedUser.id,
          name: assignedUser.name || `${assignedUser.first_name} ${assignedUser.last_name}`,
          email: assignedUser.email,
          phone: assignedUser.phone,
          avatar: assignedUser.avatar_url,
          specialty: [],
          status: 'available'
        }
      }
    }

    onSave(workOrderData)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {isEditMode ? 'Edit Work Order' : 'Create Work Order'}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                    Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className={`mt-1 block w-full border ${errors.title ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                    placeholder="Enter work order title"
                  />
                  {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description *
                  </label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className={`mt-1 block w-full border ${errors.description ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                    placeholder="Describe the work to be done"
                  />
                  {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
                </div>

                {/* Equipment */}
                <div>
                  <label htmlFor="equipment" className="block text-sm font-medium text-gray-700">
                    Equipment *
                  </label>
                  <select
                    id="equipment"
                    value={formData.equipmentId}
                    onChange={(e) => handleInputChange('equipmentId', e.target.value)}
                    className={`mt-1 block w-full border ${errors.equipmentId ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                  >
                    <option value="">Select equipment</option>
                    {equipment.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name} ({item.type})
                      </option>
                    ))}
                  </select>
                  {errors.equipmentId && <p className="mt-1 text-sm text-red-600">{errors.equipmentId}</p>}
                </div>

                {/* Priority and Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
                      Priority
                    </label>
                    <select
                      id="priority"
                      value={formData.priority}
                      onChange={(e) => handleInputChange('priority', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                      Type
                    </label>
                    <select
                      id="type"
                      value={formData.type}
                      onChange={(e) => handleInputChange('type', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="preventive">Preventive</option>
                      <option value="corrective">Corrective</option>
                      <option value="emergency">Emergency</option>
                      <option value="inspection">Inspection</option>
                    </select>
                  </div>
                </div>

                {/* Status and Assigned To */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                      Status
                    </label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="pending">Pending</option>
                      <option value="assigned">Assigned</option>
                      <option value="in-progress">In Progress</option>
                      <option value="on-hold">On Hold</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700">
                      Assign To
                    </label>
                    <select
                      id="assignedTo"
                      value={formData.assignedTo}
                      onChange={(e) => handleInputChange('assignedTo', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Unassigned</option>
                      {users.filter(user => user.role === 'technician' || user.role === 'supervisor').map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name || `${user.first_name} ${user.last_name}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Estimated Hours and Cost */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="estimatedHours" className="block text-sm font-medium text-gray-700">
                      Estimated Hours *
                    </label>
                    <input
                      type="number"
                      id="estimatedHours"
                      min="0.5"
                      step="0.5"
                      value={formData.estimatedHours}
                      onChange={(e) => handleInputChange('estimatedHours', parseFloat(e.target.value))}
                      className={`mt-1 block w-full border ${errors.estimatedHours ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                    />
                    {errors.estimatedHours && <p className="mt-1 text-sm text-red-600">{errors.estimatedHours}</p>}
                  </div>

                  <div>
                    <label htmlFor="estimatedCost" className="block text-sm font-medium text-gray-700">
                      Estimated Cost ($)
                    </label>
                    <input
                      type="number"
                      id="estimatedCost"
                      min="0"
                      step="0.01"
                      value={formData.estimatedCost}
                      onChange={(e) => handleInputChange('estimatedCost', parseFloat(e.target.value))}
                      className={`mt-1 block w-full border ${errors.estimatedCost ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                    />
                    {errors.estimatedCost && <p className="mt-1 text-sm text-red-600">{errors.estimatedCost}</p>}
                  </div>
                </div>

                {/* Due Date and Scheduled Dates */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700">
                      Due Date *
                    </label>
                    <input
                      type="date"
                      id="dueDate"
                      value={formData.dueDate}
                      onChange={(e) => handleInputChange('dueDate', e.target.value)}
                      className={`mt-1 block w-full border ${errors.dueDate ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                    />
                    {errors.dueDate && <p className="mt-1 text-sm text-red-600">{errors.dueDate}</p>}
                  </div>

                  <div>
                    <label htmlFor="scheduledStart" className="block text-sm font-medium text-gray-700">
                      Scheduled Start
                    </label>
                    <input
                      type="date"
                      id="scheduledStart"
                      value={formData.scheduledStart}
                      onChange={(e) => handleInputChange('scheduledStart', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="scheduledEnd" className="block text-sm font-medium text-gray-700">
                      Scheduled End
                    </label>
                    <input
                      type="date"
                      id="scheduledEnd"
                      value={formData.scheduledEnd}
                      onChange={(e) => handleInputChange('scheduledEnd', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Progress (for edit mode) */}
                {isEditMode && (
                  <div>
                    <label htmlFor="progress" className="block text-sm font-medium text-gray-700">
                      Progress (%)
                    </label>
                    <input
                      type="range"
                      id="progress"
                      min="0"
                      max="100"
                      value={formData.progress}
                      onChange={(e) => handleInputChange('progress', parseInt(e.target.value))}
                      className="mt-1 block w-full"
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-1">
                      <span>0%</span>
                      <span className="font-medium">{formData.progress}%</span>
                      <span>100%</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}